<script lang="ts">
import { defineComponent, reactive, inject } from 'vue'
import stateInitializer from '@/models/stateInitializer'
import { staticPagesOptoutPath } from '@/generated/railsRoutes'

export default defineComponent({
  name: 'PrivacyMain',

  setup() {
    // constants
    const EXTERNAL_URL = inject<Record<string, string>>('EXTERNAL_URL')

    interface State {
      userTrackingServices: Array<{
        company: string,
        service: string,
        url: string,
      }>
      networkServices: Array<{
        name: string,
        company: string,
        contents: string[],
        purposes: string[],
      }>
    }
    const state: State = reactive({
      userTrackingServices: [],
      networkServices: [],
    })
    stateInitializer(state, 'main')

    return {
      // constants
      EXTERNAL_URL,

      // routes
      staticPagesOptoutPath,

      // data
      state,
    }
  }
})
</script>

<template>
  <!--
    ホワイトスペースが文頭に入らないように、dt 直後に改行せず、一行で記載している。
  -->
  <article class="article">
    <section class="section">
      <h1 class="h1 u-text__center">
        個人情報の取り扱いについて
      </h1>

      <p class="p u-text__right">
        株式会社ビズヒント<br>
        代表取締役社長　関 哲
      </p>

      <p class="p">
        株式会社ビズヒント（以下「当社」といいます）は、当社が運営する各種サービス（以下「本サービス」といいます）を利用するお客様（本サービスについてお問い合わせいただいた方を含み、以下「利用者」といいます）の個人情報を取得、利用及び管理するにあたっては細心の注意を払い、以下のとおりに取り扱います。
      </p>

      <div class="o-list__ordered">
        <dl class="o-list__ordered--unit">
          <dt class="o-list__ordered--term">用語の定義について</dt>
          <dd class="o-list__ordered--description">
            個人情報等の定義は「個人情報の保護に関する法律（平成15年法律第57号）」の定めるところに従うものとします。
          </dd>

          <dt class="o-list__ordered--term">個人情報の利用目的について</dt>
          <dd class="o-list__ordered--description">
            当社が本サービスの会員登録及び本サービスの利用に伴って取得する利用者の個人情報の利用目的は以下のとおりです。<br>
            <div
              class="o-list__ordered u-margin__smaller"
              data-has-bracket="true">
              <ul class="o-list__ordered--items">
                <li class="o-list__ordered--item">当社サービスの会員登録、運営管理、事務連絡及び各種システム機能のご提供に使用するため</li>
                <li class="o-list__ordered--item">当社と提携した国内及び国外の事業者又は企業に情報を提供するため（取得した情報を分析又は解析して提供することを含みます）</li>
                <li class="o-list__ordered--item">取得した情報を分析又は解析して、当社サービスの運営管理および改善ならびに新サービスの開発に利用するため</li>
                <li class="o-list__ordered--item">当社、スマートキャンプグループ各社及び第三者のサービス・イベント・セミナー等のご案内、運営管理、広報活動に利用するため（取得した情報を分析又は解析して利用することを含みます）</li>
                <li class="o-list__ordered--item">当社、スマートキャンプグループ各社及び第三者の商品等の広告・宣伝、販売の勧誘・広報活動に利用するため（取得した情報を分析又は解析して利用することを含みます）</li>
                <li class="o-list__ordered--item">アンケート、キャンペーン等の依頼、連絡、プレゼント発送等を行うため</li>
                <li class="o-list__ordered--item">メールマガジン等の情報を配信するため（取得した情報を分析又は解析して利用することを含みます）</li>
                <li class="o-list__ordered--item">当社サービスについてのお問い合わせ、質問に対する回答を行うため</li>
                <li class="o-list__ordered--item">当社が提供するサービスの保守・メンテナンス業務を行うため </li>
                <li class="o-list__ordered--item">上記の（１）から（９）のほか、個人情報の取得に際して個別に利用目的を明示又は通知したときは、その利用目的に必要な範囲で利用するため</li>
              </ul>
            <!-- /.o-list__ordered --></div>
          </dd>

          <dt class="o-list__ordered--term">個人情報提供の任意性について</dt>
          <dd class="o-list__ordered--description">
            個人情報の当社への提供は任意ですが、必要な情報が提供されない場合、会員登録及び本サービスの利用やお問い合わせへの回答ができない場合もありうることをご了承ください。
          </dd>

          <dt class="o-list__ordered--term">個人データの第三者への提供について</dt>
          <dd class="o-list__ordered--description">
            <p class="p">
              当社は下記の場合に個人データを第三者に提供いたします。<br>
              なお、下記に記載がない場合もご本人の同意を得たうえで個人データを第三者に提供することがあります。
            </p>

            <p class="p">
              個人データの提供は、特に記述のない限り、暗号化等を施したウェブシステム、電子メール、書面の手交のいずれかによるものとします。
            </p>

            <div
              class="o-list__ordered u-margin__smaller"
              data-has-bracket="true">
              <ul class="o-list__ordered--items">
                <li class="o-list__ordered--item">サービス提供のため、会員登録時に入力いただいた情報を、提携した国内及び国外の事業者又は企業に提供する場合があります。</li>
                <li class="o-list__ordered--item">当社は「2.個人情報の利用目的について」で述べた目的を達成するため、個人データを外国の第三者に提供する可能性があります。</li>
                <li class="o-list__ordered--item">当社は、原則として、利用者の同意のない限り、第三者に個人データを提供することはありません。ただし、以下の場合には、利用者の同意なく個人データを提供することがあります。<br>
                  <div class="o-list__ordered u-margin__smaller">
                    <ul class="o-list__ordered--items">
                      <li class="o-list__ordered--item">法令に基づく場合</li>
                      <li class="o-list__ordered--item">人の生命、身体又は財産の保護のために必要がある場合であって、本人の同意を得ることが困難であるとき</li>
                      <li class="o-list__ordered--item">公衆衛生の向上又は児童の健全な育成の推進のために特に必要がある場合であって、本人の同意を得ることが困難であるとき</li>
                      <li class="o-list__ordered--item">国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、本人の同意を得ることによって当該事務の遂行に支障を及ぼすおそれがあるとき</li>
                    </ul>
                  <!-- /.o-list__ordered --></div>
                </li>
              </ul>
            <!-- /.o-list__ordered --></div>
          </dd>

          <dt class="o-list__ordered--term">第三者による個人情報の取得等に関する免責事項について</dt>
          <dd class="o-list__ordered--description">
            以下の場合は、第三者による個人情報の取得、利用及び管理に関し、当社は何らの責任を負いません。<br>
            <div
              class="o-list__ordered u-margin__smaller"
              data-has-bracket="true">
              <ul class="o-list__ordered--items">
                <li class="o-list__ordered--item">
                  利用者自らが本サービスの機能又は別の手段を用いて事業者、求人企業等の第三者に個人情報を明らかにする場合
                </li>
                <li class="o-list__ordered--item">
                  利用者の活動情報又は利用者が本サービスの利用に関して投稿等した情報により、第三者に利用者本人が特定されるにいたった場合
                </li>
                <li class="o-list__ordered--item">
                  本サービスからリンクされる外部サイトにおいて、利用者より個人情報が提供され、またそれが利用された場合
                </li>
                <li class="o-list__ordered--item">
                  利用者本人以外が利用者個人を識別できる情報（ID、パスワード等）を入手した場合
                </li>
                <li class="o-list__ordered--item">
                  当社に責めに帰すべき事由がない場合
                </li>
              </ul>
            <!-- /.o-list__ordered --></div>
          </dd>

          <dt class="o-list__ordered--term">個人データの共同利用について</dt>
          <dd class="o-list__ordered--description">
            当社は、当社の国内グループ会社との間で、当社が取得した個人データを共同利用する場合があります。<br>
            <div
              class="o-list__ordered u-margin__smaller"
              data-has-bracket="true">
              <dl class="o-list__ordered--unit">
                <dt class="o-list__ordered--term">共同利用する個人データの項目</dt>
                <dd class="o-list__ordered--description">
                  <div class="o-list__ordered">
                    <ul class="o-list__ordered--items">
                      <li class="o-list__ordered--item">氏名、事業者名、住所、メールアドレス、電話番号その他の個人の属性に関する情報（ただし、ユーザーID及びパスワード等の認証情報を除きます）</li>
                      <li class="o-list__ordered--item">当社のサービスの利用履歴に関する情報</li>
                      <li class="o-list__ordered--item">当社サービスの利用者が当社のサービスに送信又は保存することにより取得するデータ</li>
                      <li class="o-list__ordered--item">前三号のデータを分析又は解析することにより得られるデータ</li>
                    </ul>
                  <!-- /.o-list__ordered --></div>
                </dd>

                <dt class="o-list__ordered--term">共同利用する範囲</dt>
                <dd class="o-list__ordered--description">
                  当社の親会社及びその国内グループ会社（以下「当社国内グループ会社」といいます）<br>
                  <div class="o-note__general u-margin__smaller">
                    <p class="o-note__general--item">
                      <small class="small">
                        当社のグループ会社
                        <a
                          class="a"
                          :href="EXTERNAL_URL!.smartcamp_corporate_url"
                          target="_blank">
                          {{ EXTERNAL_URL!.smartcamp_corporate_url }}
                        </a>
                      </small>
                    </p>
                  <!-- /.o-note__general --></div>
                </dd>

                <dt class="o-list__ordered--term">共同利用する目的</dt>
                <dd class="o-list__ordered--description">
                  <div class="o-list is-circle">
                    <ul class="o-list--items">
                      <li class="o-list--item">
                        当社国内グループ会社及び第三者の商品、サービスの広告・宣伝、販売やイベント・セミナー等のご案内、運営管理及び広報活動に利用するため
                      </li>
                      <li class="o-list--item">
                        データを分析又は解析して、前号の目的及び当社国内グループ会社におけるサービスの改善や向上、新規サービスの開発に利用するため
                      </li>
                    </ul>
                  <!-- /.o-list --></div>
                </dd>

                <dt class="o-list__ordered--term">個人データの管理について責任を有する者</dt>
                <dd class="o-list__ordered--description">
                  株式会社ビズヒント<br>
                  東京都港区芝5丁目29−11　G-BASE田町13階<br>
                  <div class="o-list__record is-colon u-margin__smaller">
                    <dl class="o-list__record--unit">
                      <dt class="o-list__record--term">代表者</dt>
                      <dd class="o-list__record--description">代表取締役　関　哲</dd>
                    </dl>
                  <!-- /.o-list__record --></div>
                </dd>
              </dl>
            <!-- /.o-list__ordered --></div>
          </dd>

          <dt class="o-list__ordered--term">個人データの委託について</dt>
          <dd class="o-list__ordered--description">
            当社は事業運営上、業務委託先に個人データの取り扱いを委託することがあります。この場合、当社は、個人データを適切に保護できる管理体制を敷き実行していることを条件として委託先を厳選したうえで、契約等において個人データの適正管理・機密保持等により利用者の個人データの漏洩防止に必要な事項を取決め、適切な管理を実施させます。
          </dd>

          <dt class="o-list__ordered--term">仮名加工情報の作成について</dt>
          <dd class="o-list__ordered--description">
            当社は取得した情報をもとに仮名加工情報を作成し、以下の目的のため利用することがあります。
            <div class="o-list is-circle">
              <ul class="o-list--items">
                <li class="o-list--item">
                  当社サービスの改善及び新サービスの開発のため
                </li>
              </ul>
            <!-- /.o-list --></div>
          </dd>

          <dt class="o-list__ordered--term">通話記録の保存について</dt>
          <dd class="o-list__ordered--description">
            当社は、通話内容の確認、サービス品質の向上を目的として電話等による通話記録を保存させていただく場合があります。
          </dd>

          <dt class="o-list__ordered--term">統計処理されたデータの利用について</dt>
          <dd class="o-list__ordered--description">
            当社は、取得した個人情報をもとに、個人との対応関係を排斥した統計情報を作成することがあります。統計情報については、当社は何ら制限なく利用することができるものとします。
          </dd>

          <dt class="o-list__ordered--term">アプリケーション・プライバシーポリシー</dt>
          <dd class="o-list__ordered--description">
            <div
              class="o-list__ordered"
              data-has-bracket="true">
              <dl class="o-list__ordered--unit">
                <dt class="o-list__ordered--term">自動的に取得する情報</dt>
                <dd class="o-list__ordered--description">
                  サービス用アプリケーションのご利用に際して、利用者情報を以下の利用目的のためにアプリケーション経由で自動的に取得いたします。<br>
                  <div class="o-list__record is-colon u-margin__smaller">
                    <dl class="o-list__record--unit">
                      <dt class="o-list__record--term">利用目的</dt>
                      <dd class="o-list__record--description">サービスによる情報提供性能向上のため</dd>
                    </dl>

                    <dl class="o-list__record--unit">
                      <dt class="o-list__record--term">取得する情報の項目</dt>
                      <dd class="o-list__record--description">アプリ使用履歴、広告ID</dd>
                    </dl>
                  <!-- /.o-list__record --></div>
                </dd>

                <dt class="o-list__ordered--term">情報収集モジュール</dt>
                <dd class="o-list__ordered--description">
                  本サービスの提供にあたって、他社の情報収集モジュールを本サービスの利用状況の解析と広告効果測定の目的で利用し、ご利用者の情報を自動取得します。<br>
                  情報収集モジュールの詳細と各社の利用目的は、以下に記載されている各社のアプリケーション・プライバシーポリシーのリンクよりご確認ください。
                  <div
                    v-for="userTrackingService in state.userTrackingServices"
                    :key="userTrackingService.company"
                    class="o-list__record is-colon u-margin__smaller">
                    <dl class="o-list__record--unit">
                      <dt class="o-list__record--term">会社名</dt>
                      <dd class="o-list__record--description">
                        {{ userTrackingService.company }}
                      </dd>
                    </dl>

                    <dl class="o-list__record--unit">
                      <dt class="o-list__record--term">モジュール名</dt>
                      <dd class="o-list__record--description">
                        {{ userTrackingService.service }}
                      </dd>
                    </dl>

                    <dl class="o-list__record--unit">
                      <dt class="o-list__record--term">HP</dt>
                      <dd class="o-list__record--description">
                        {{ userTrackingService.url }}
                      </dd>
                    </dl>
                  <!-- /.o-list__record --></div>
                </dd>
              </dl>
            <!-- /.o-list__ordered --></div>
          </dd>

          <dt class="o-list__ordered--term">安全管理について</dt>
          <dd class="o-list__ordered--description">
            当社は、個人データ（当社が取得し、又は取得しようとしている個人情報であって、当社が個人データとして取り扱うことを予定しているものを含みます。）の安全管理のために以下の措置を講じています。<br>
            <div
              class="o-list__ordered u-margin__smaller"
              data-has-bracket="true">
              <dl class="o-list__ordered--unit">
                <dt class="o-list__ordered--term">組織的安全管理措置</dt>
                <dd class="o-list__ordered--description">
                  <div class="o-list is-circle">
                    <ul class="o-list--items">
                      <li class="o-list--item">個人データの取り扱いに関する責任者を設置し、事故等の報告窓口を整備しています。</li>
                      <li class="o-list--item">個人データの棚卸しや個人データの取り扱いに関する内部監査を実施しています。</li>
                    </ul>
                  <!-- /.o-list --></div>
                </dd>

                <dt class="o-list__ordered--term">人的安全管理措置</dt>
                <dd class="o-list__ordered--description">
                  <div class="o-list is-circle">
                    <ul class="o-list--items">
                      <li class="o-list--item">全従業員向けに個人データの取り扱いに関する研修を実施しています。</li>
                      <li class="o-list--item">個人データについての秘密保持に関する事項を就業規則に規定し、また入社時に従業員から誓約書を徴求しています。</li>
                    </ul>
                  <!-- /.o-list --></div>
                </dd>

                <dt class="o-list__ordered--term">物理的安全管理措置</dt>
                <dd class="o-list__ordered--description">
                  <div class="o-list is-circle">
                    <ul class="o-list--items">
                      <li class="o-list--item">個人データを扱う機器、電子媒体は盗難防止策及び暗号化を実施しています。</li>
                      <li class="o-list--item">個人データを取り扱う執務室は入退出制限を実施しています。</li>
                    </ul>
                  <!-- /.o-list --></div>
                </dd>

                <dt class="o-list__ordered--term">技術的安全管理措置</dt>
                <dd class="o-list__ordered--description">
                  <div class="o-list is-circle">
                    <ul class="o-list--items">
                      <li class="o-list--item">個人データのアクセス制御を実施し、必要最小限の担当者のみが個人データにアクセスする体制を整備しています。</li>
                      <li class="o-list--item">個人データを取り扱う情報システムを不正アクセス等から保護する仕組みを導入しています。</li>
                    </ul>
                  <!-- /.o-list --></div>
                </dd>

                <dt class="o-list__ordered--term">外的環境の把握</dt>
                <dd class="o-list__ordered--description">
                  クラウドサービス上で個人データを保管している以下の国における個人情報の保護に関する制度を把握したうえで安全管理措置を実施いたします。<br>
                  <div class="o-list is-circle">
                    <dl class="o-list--unit">
                      <dt class="o-list--term">クラウド業者所在国名</dt>
                      <dd class="o-list--description">
                        アメリカ合衆国
                      </dd>
                    </dl>

                    <dl class="o-list--unit">
                      <dt class="o-list--term">データ保管国</dt>
                      <dd class="o-list--description">
                        アメリカ合衆国
                      </dd>
                    </dl>
                  <!-- /.o-list --></div>
                  <div class="o-note__general">
                    <p class="o-note__general--item">一部のクラウドサービスについてはデータの保管場所が公開されておらず、データ保管国が特定できない場合があります。</p>
                  <!-- /.o-note__general --></div>
                </dd>
              </dl>
            <!-- /.o-list__ordered --></div>
          </dd>

          <dt class="o-list__ordered--term">本人確認について</dt>
          <dd class="o-list__ordered--description">
            当社は、利用者の会員登録の場合や利用者が本サービスを利用する場合のほか、個人データの利用目的の通知、開示（第三者提供記録の開示を含みます）、訂正等（内容の訂正、追加もしくは削除）、利用停止等（利用の停止もしくは消去）または第三者の提供の停止（以下「開示等」といいます）の求めに応じる場合、お問い合わせへ回答する場合等に、個人を識別できる情報（氏名、電話番号、メールアドレス、パスワード等）により、本人であることを確認します。ただし、本人以外が個人を識別できる情報を入手し使用した場合、当社は責任を負いません。
          </dd>

          <dt class="o-list__ordered--term">個人関連情報の取得及び利用について</dt>
          <dd class="o-list__ordered--description">
            <p class="p">
              当社は、利用者のプライバシーの保護、利便性の向上、広告の配信、及び統計データの取得のため、Cookieを使用します。また、当社は、CookieやJavaScript等の技術を利用して、サイト内におけるユーザーの行動履歴（アクセスしたURL、コンテンツ、参照順等）を取得することがあります。ただし、行動履歴には個人情報は一切含まれません。<br>
              なお、Cookieの受け取りは、ブラウザの設定によって拒否することができます。拒否した場合、システムをご利用いただくうえで一部機能に制約が生じることがあります。
            </p>

            <p class="p">
              取得した属性情報及び行動履歴等個人を特定できない情報について、当社は何ら制限なく利用することができるものとします。<br>
              <a
                class="a"
                :href="staticPagesOptoutPath()">
                Cookieを利用した行動ターゲティング広告について
              </a>
            </p>
          </dd>

          <dt class="o-list__ordered--term">開示等の求めに応じる手続きについて</dt>
          <dd class="o-list__ordered--description">
            個人データの開示等の求めにつき、当社は、当社の保有する個人データについては、本人確認をおこなった後、合理的な範囲で速やかに対応いたします。ただし、他の利用者その他第三者の生命・身体・財産その他の利益を害するおそれのある場合、当社の業務遂行に著しく支障をきたすと判断した場合、対応することが法令に違反する場合には、この限りではありません。開示等の請求を行う場合、詳細な手続きについては下記のお問い合わせ先までご連絡ください。
          </dd>

          <dt class="o-list__ordered--term">「個人情報の取り扱いについて」の変更について</dt>
          <dd class="o-list__ordered--description">
            当社は、「個人情報の取り扱いについて」を法令に違反しない範囲で任意に変更することができるものとします。
          </dd>

          <dt class="o-list__ordered--term">個人情報保護管理者及びお問い合わせ先</dt>
          <dd class="o-list__ordered--description">
            <p class="p">
              当社は、次の者を個人情報保護管理者に任命し、利用者の個人情報を適切かつ安全に管理し、外部からの不正アクセス、紛失、破壊、改ざん、漏えい等を予防及び是正するための対策を講じています。
            </p>

            <div class="o-list__record is-colon u-margin__smaller">
              <dl class="o-list__record--unit">
                <dt class="o-list__record--term">
                  株式会社ビズヒント　個人情報保護管理者
                </dt>
                <dd class="o-list__record--description">
                  代表取締役社長
                </dd>
              </dl>
            <!-- /.o-list__record --></div>

            <p class="p">
              個人情報に関する苦情及び相談、開示等についての当社お問い合わせ先は、次のとおりです。
            </p>

            <div class="div">
              株式会社ビズヒント<br>
              <div class="o-list__record is-colon u-margin__smaller">
                <dl class="o-list__record--unit">
                  <dt class="o-list__record--term">住所</dt>
                  <dd class="o-list__record--description">
                    〒108-0014 東京都港区芝5丁目29−11　G-BASE田町13階
                  </dd>
                </dl>

                <dl class="o-list__record--unit">
                  <dt class="o-list__record--term">電話</dt>
                  <dd class="o-list__record--description">
                    03-4540-6232
                  </dd>
                </dl>
              <!-- /.o-list__record --></div>
            </div>
          </dd>
        </dl>
      <!-- /.o-list__ordered --></div>

      <div class="o-list__record is-colon is-right">
        <dl
          class="o-list__record--unit u-text__before u-text__after"
          data-before="("
          data-after=")">
          <dt class="o-list__record--term">最終改定日</dt>
          <dd class="o-list__record--description">
            <time
              class="time"
              datetime="2025-11-07">
              2025年11月7日
            </time>
          </dd>
        </dl>
      <!-- /.o-list__record --></div>
    </section>

    <hr class="hr">

    <section class="section">
      <h2 class="h2 u-text__center">
        電気通信事業法による利用者情報の外部送信に関する開示
      </h2>

      <p class="p">
        電気通信事業法第27条の12及び同施行規則第22条の2の29により、開示が求められる情報送信指令通信※1が起動させる情報送信機能※2により送信されることとなる利用者に関する情報の内容、情報の送信先となる電気通信設備を用いて当該情報を取り扱うこととなる者の氏名又は名称、及び情報の利用目的につきましては、以下に掲げるとおりです。
      </p>

      <div
        class="o-note__ordered"
        data-is-manual-number="true">
        <p class="o-note__ordered--item">
          <span class="o-note__ordered--label is-marker">※1</span>
          <span class="o-note__ordered--label is-body">利用者のパーソナルコンピューター、スマートフォン、タブレット等の情報端末が有する情報送信機能を起動する指令となるプログラム等の送信であり、具体的には、利用者に関する情報を利用者の情報端末から外部に送信させ収集するための仕組みを実現するコード等の情報の送信が含まれます。</span>
        </p>
      <!-- /.o-note__ordered --></div>

      <p class="p">
        なお、ウェブサイトの場合については、HTML、CSS、JavaScript等の言語で記述されたウェブサイトを構成するソースコードのうち上記仕組みを実現する部分、アプリケーションの場合については、アプリケーションに埋め込まれている情報収集モジュール等の情報送信機能の起動の契機となるプログラム等が含まれますが、これらに限られません。
      </p>

      <div
        class="o-note__ordered"
        data-is-manual-number="true">
        <p class="o-note__ordered--item">
          <span class="o-note__ordered--label is-marker">※2</span>
          <span class="o-note__ordered--label is-body">利用者のパーソナルコンピューター、スマートフォン、タブレット等の情報端末に記録された当該利用者に関する情報を当該利用者以外の者のパーソナルコンピューター、スマートフォン、タブレット等の情報端末に送信する機能をいいます。</span>
        </p>
      <!-- /.o-note__ordered --></div>

      <p class="p">
        {{ state.networkService }}
      </p>

      <div
        class="div"
        style="overflow-x: auto;">
        <table class="table u-block__min820">
          <colgroup class="colgroup">
            <col class="col">
            <col class="col">
            <col class="col">
            <col class="col">
          </colgroup>

          <thead class="thead">
            <tr class="tr">
              <th class="th">サービス名</th>
              <th class="th">送信先の名称</th>
              <th class="th">送信される情報</th>
              <th class="th">情報の利用目的</th>
            </tr>
          </thead>

          <tbody class="tbody">
            <tr
              v-for="networkService in state.networkServices"
              :key="networkService.name"
              class="tr">
              <th class="th">{{ networkService.name }}</th>
              <td class="td">{{ networkService.company }}</td>
              <td class="td">
                <div class="o-list is-circle">
                  <ul class="o-list--items">
                    <li
                      v-for="content in networkService.contents"
                      :key="content"
                      class="o-list--item">
                      {{ content }}
                    </li>
                  </ul>
                <!-- /.o-list --></div>
              </td>
              <td class="td">
                <div class="o-list is-circle">
                  <ul class="o-list--items">
                    <li
                      v-for="purpose in networkService.purposes"
                      :key="purpose"
                      class="o-list--item">
                      {{ purpose }}
                    </li>
                  </ul>
                <!-- /.o-list --></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </article>
</template>
